import { skipToken } from "@tanstack/react-query";
import type { RouterInputs, RouterOutputs } from "@remi/api";
import { SITE_ACCESS_FIELDS } from "@remi/types/worksite";

import { api } from "~/utils/api";

export type WorksiteById = RouterOutputs["bid"]["getWorkSiteById"];
export const useWorksite = (id?: number) => {
  return api.bid.getWorkSiteById.useQuery(id !== undefined ? { id } : skipToken);
};

// "Present" means `!== undefined`, matching the write path: an optional form field (e.g. an unselected
// HOA dropdown) sends a present-but-undefined key that JSON drops before the backend ever sees it, so
// `key in input` would wrongly clobber the cached value for a field the backend leaves untouched.
export const mergeOptimisticWorksiteUpdate = (
  previousValueById: NonNullable<WorksiteById>,
  worksite: RouterInputs["bid"]["updateWorkSite"],
): NonNullable<WorksiteById> => {
  const { confirmedWholeBuilding: _confirmedWholeBuilding, ...propertyAddressInput }: Record<string, unknown> =
    worksite.propertyAddress ?? {};
  const siteAccessOverride: Record<string, unknown> = {};
  const propertyAddressOverride: Record<string, unknown> = { ...propertyAddressInput };
  for (const key of SITE_ACCESS_FIELDS) {
    if (propertyAddressInput[key] !== undefined) {
      siteAccessOverride[key] = propertyAddressInput[key];
    }
    delete propertyAddressOverride[key];
  }
  // oxlint-disable-next-line remi/no-type-assertions -- Zod v4 intersection type differs from tRPC output in optimistic update
  return {
    ...previousValueById,
    ...worksite,
    ...siteAccessOverride,
    propertyAddress: {
      ...previousValueById.propertyAddress,
      ...propertyAddressOverride,
    },
    homeowner: {
      ...previousValueById.homeowner,
      ...worksite.homeowner,
    },
  } as never;
};

export const useWorksiteUpdate = () => {
  const { bid: bidRoute } = api.useUtils();

  const update = api.bid.updateWorkSite.useMutation({
    onMutate: async (worksite) => {
      await bidRoute.getWorkSiteById.cancel({ id: worksite.id });
      const previousValueById = bidRoute.getWorkSiteById.getData({ id: worksite.id });
      if (previousValueById) {
        bidRoute.getWorkSiteById.setData(
          { id: worksite.id },
          mergeOptimisticWorksiteUpdate(previousValueById, worksite),
        );
      }
      return { previousValueById };
    },
    onError: (_, variables, context) => {
      if (context?.previousValueById) {
        bidRoute.getWorkSiteById.setData({ id: variables.id }, context.previousValueById);
      }
    },
    onSettled: async (_, _error, { id }) => {
      await bidRoute.getWorkSiteById.invalidate({ id });
    },
  });
  return update;
};
